import React from 'react';
import {
  ActivityIndicator,
  Box,
  FrameworkImage,
  GridContainerWithTitle,
  HorizontalDivider,
  Button,
  ArrowRightIcon,
  Typography,
  VerticalDivider,
} from '@ttx/design-system';
import {useBreakpoint} from '@ttx/design-system/src/use-breakpoint';

const ProcessingModalInfo = ({
  copyPrimary,
  copyPrimary2,
  copySecondary,
  withProcessingIndicator,
  bottomTransactionHash,
  artist,
  trackName,
  quantity,
  isMobile,
}: ModalWrapperProps) => {
  return (
    <>
      <Box>
        <Typography color="textLow" textStyle="s">
          {copySecondary}
        </Typography>
        <Box mt="s" />
        <Typography color="textDefault" textStyle="s">
          {copyPrimary}
        </Typography>
        {!!copyPrimary2 && (
          <Typography color="textDefault" textStyle="s">
            {copyPrimary2}
          </Typography>
        )}
      </Box>
      <Box>
        {withProcessingIndicator && (
          <Box
            flexDirection="row"
            alignItems="center"
            paddingTop={isMobile ? 'four' : 'zero'}
          >
            <ActivityIndicator size={14} />
            <Box w="s" />
            <Typography color="textDefault" textStyle="s">
              Processing...
            </Typography>
          </Box>
        )}
        {!!artist && !!trackName && !!quantity && (
          <Typography color="textDefault" textStyle="s">
            {`${artist}, ${trackName} `}
            <Typography color="textLow" textStyle="s">
              {`x${quantity}`}
            </Typography>
          </Typography>
        )}
        {!!bottomTransactionHash && (
          <Box mt="xs">
            <Typography color="textLow" textStyle="s">
              {bottomTransactionHash}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export interface ModalWrapperProps {
  titleIcon?: JSX.Element;
  title: string;
  copyPrimary: string;
  copyPrimary2?: string;
  copySecondary: string;
  withProcessingIndicator?: boolean;
  imageSrc?: string;
  singleButton?: {
    label: string;
    handlePress: () => void;
  };
  leftButton?: {
    label: string;
    handlePress: () => void;
  };
  rightButton?: {
    label: string;
    handlePress: () => void;
    type?: string;
  };
  bottomTransactionHash?: string;
  artist?: string;
  trackName?: string;
  quantity?: number;
  onModalClose: () => void;
  isMobile?: boolean;
}

export const ModalWrapper: React.FC<ModalWrapperProps> = ({
  titleIcon,
  title,
  copyPrimary,
  copyPrimary2,
  copySecondary,
  withProcessingIndicator,
  imageSrc,
  singleButton,
  leftButton,
  rightButton,
  bottomTransactionHash,
  artist,
  trackName,
  quantity,
  onModalClose,
}: ModalWrapperProps) => {
  const {currentBreakpoint} = useBreakpoint();
  const isMobile = currentBreakpoint === '768px';

  return (
    <GridContainerWithTitle
      titleIcon={titleIcon}
      title={title}
      onModalClose={onModalClose}
      maxWidth={600}
    >
      {isMobile ? (
        <Box>
          <Box>
            <FrameworkImage
              width={366}
              height={366}
              src={imageSrc || ''}
              alt={imageSrc}
            />
          </Box>
          <HorizontalDivider />
          <Box m="lg" justifyContent="space-between">
            <ProcessingModalInfo
              bottomTransactionHash={bottomTransactionHash}
              artist={artist}
              trackName={trackName}
              quantity={quantity}
              copyPrimary={copyPrimary}
              copyPrimary2={copyPrimary2}
              copySecondary={copySecondary}
              withProcessingIndicator={withProcessingIndicator}
              title={title}
              isMobile={isMobile}
              onModalClose={onModalClose}
            />
          </Box>
        </Box>
      ) : (
        <Box flexDirection="row">
          <Box>
            <FrameworkImage
              src={imageSrc || ''}
              alt={imageSrc}
              width={203}
              height={203}
            />
          </Box>
          <VerticalDivider />
          <Box m="lg" flex={1} justifyContent="space-between">
            <ProcessingModalInfo
              bottomTransactionHash={bottomTransactionHash}
              artist={artist}
              trackName={trackName}
              quantity={quantity}
              copyPrimary={copyPrimary}
              copyPrimary2={copyPrimary2}
              copySecondary={copySecondary}
              withProcessingIndicator={withProcessingIndicator}
              title={title}
              isMobile={isMobile}
              onModalClose={onModalClose}
            />
          </Box>
        </Box>
      )}
      {!!singleButton && (
        <>
          <HorizontalDivider />
          <Box flex={1} flexDirection="row">
            <Button
              hasBorder
              type="primary"
              onPress={singleButton.handlePress}
              buttonStyle={{margin: '8px 6px 8px 3px'}}
              leftAccessory={<ArrowRightIcon color="surfaceDark" />}
            >
              {singleButton.label}
            </Button>
          </Box>
        </>
      )}
      {!!leftButton && !!rightButton && (
        <>
          <HorizontalDivider />
          <Box flex={1} flexDirection="row">
            <Box flex={1}>
              <Button
                hasBorder
                type="secondary"
                buttonStyle={{margin: '8px 6px 8px 3px'}}
                onPress={leftButton.handlePress}
                leftAccessory={<ArrowRightIcon color="textDefault" />}
              >
                {leftButton.label}
              </Button>
            </Box>
            <Box
              flex={1}
              // borderLeftWidth="one"
              // borderLeftColor="outlines"
              // borderLeftStyle="solid"
            >
              <Button
                hasBorder={rightButton.type}
                type={rightButton.type || 'primary'}
                buttonStyle={{margin: '8px 6px 8px 3px'}}
                onPress={rightButton.handlePress}
                leftAccessory={<ArrowRightIcon color="textDefault" />}
              >
                {rightButton.label}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </GridContainerWithTitle>
  );
};
